import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Popconfirm, message, Tag, Typography, Card, Modal, Select, Form, DatePicker, Row, Col, Radio, Checkbox, Divider, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined, SendOutlined, RocketOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import moment from 'moment';

const QUICK_TEMPLATES = [
    {
        id: 'dorm',
        title: '住宿生留宿登记',
        description: '周末及节假日住宿生留宿意向统计',
        target: 'wechat_parent',
        fields: [
            { id: 'field_1', label: '本周末是否留宿', type: 'radio', options: ['是', '否'], required: true },
            { id: 'field_2', label: '备注说明（如特殊情况）', type: 'text', required: false }
        ]
    },
    {
        id: 'uniform',
        title: '校服尺码统计',
        description: '新生或新学期校服采购尺码收集',
        target: 'wechat_parent',
        fields: [
            { id: 'field_1', label: '学生身高 (cm)', type: 'number', required: true },
            { id: 'field_2', label: '选择校服尺码', type: 'radio', options: ['S (150-160cm)', 'M (160-170cm)', 'L (170-175cm)', 'XL (175-180cm)', 'XXL (180cm以上)'], required: true }
        ]
    },
    {
        id: 'afterschool',
        title: '课后托管项目报名',
        description: '本学期课后延时服务及兴趣班报名',
        target: 'wechat_parent',
        fields: [
            { id: 'field_1', label: '是否参加课后托管', type: 'radio', options: ['参加', '不参加'], required: true },
            { id: 'field_2', label: '意向参加的项目 (可选多项)', type: 'checkbox', options: ['晚自习基础托管 (18:00-20:00)', '周末体育特长班', '周末艺术兴趣班', '学科培优班'], required: false }
        ]
    },
    {
        id: 'camp',
        title: '研学活动报名统计',
        description: '即将开展的校外研学实践活动报名',
        target: 'wechat_parent',
        fields: [
            { id: 'field_1', label: '是否同意孩子参加本次研学', type: 'radio', options: ['同意参加', '不参加'], required: true },
            { id: 'field_2', label: '家长是否随行志愿者', type: 'radio', options: ['是', '否'], required: false },
            { id: 'field_3', label: '意向路线选择', type: 'radio', options: ['路线A: 省科技馆探索之旅', '路线B: 历史博物馆文化之旅', '路线C: 现代农业科普园生态之旅'], required: false }
        ]
    },
    {
        id: 'health',
        title: '学生体质健康调查',
        description: '学生基本健康状况及特异体质收集',
        target: 'wechat_parent',
        fields: [
            { id: 'field_1', label: '是否有特异体质或过敏史', type: 'radio', options: ['有', '无'], required: true },
            { id: 'field_2', label: '具体过敏源或体质说明（如有）', type: 'text', required: false }
        ]
    },
    {
        id: 'volunteer',
        title: '志愿者报名',
        description: '校园活动、社区服务志愿者招募统计',
        target: 'wechat_parent',
        fields: [
            { id: 'field_1', label: '可参与服务的时间段（可选多项）', type: 'checkbox', options: ['周五下午', '周六上午', '周六下午', '周日上午', '随时可协调'], required: true },
            { id: 'field_2', label: '特长或意向岗位说明', type: 'text', required: false }
        ]
    },
    {
        id: 'sports',
        title: '校运会报名',
        description: '学校春/秋季运动会参赛项目报名',
        target: 'desktop',
        fields: [
            { id: 'field_1', label: '参赛项目1（必报）', type: 'radio', options: ['100米短跑', '400米', '800米', '跳高', '跳远', '铅球'], required: true },
            { id: 'field_2', label: '参赛项目2（选报）', type: 'radio', options: ['无', '100米短跑', '400米', '800米', '跳高', '跳远', '铅球'], required: false }
        ]
    }
];

const { Title } = Typography;
const { Option } = Select;

const CollectionList = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pushModalVisible, setPushModalVisible] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [classes, setClasses] = useState([]);
    const [pushing, setPushing] = useState(false);
    const [form] = Form.useForm();

    const [quickModalVisible, setQuickModalVisible] = useState(false);
    const [quickForm] = Form.useForm();
    const [quickPushing, setQuickPushing] = useState(false);

    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewTask, setPreviewTask] = useState(null);

    const showPreviewModal = (record) => {
        let taskCopy = { ...record };
        if (typeof taskCopy.fields === 'string') {
            try { taskCopy.fields = JSON.parse(taskCopy.fields); } catch (e) { taskCopy.fields = []; }
        }
        setPreviewTask(taskCopy);
        setPreviewModalVisible(true);
    };

    const navigate = useNavigate();

    useEffect(() => {
        fetchTasks();
        fetchClasses();
    }, []);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const response = await api.get('/data-collection/tasks');
            if (response.data.success) {
                setTasks(response.data.data);
            } else {
                message.error('获取任务列表失败: ' + response.data.message);
            }
        } catch (error) {
            console.error('获取任务列表出错:', error);
            message.error('获取任务列表出错');
        } finally {
            setLoading(false);
        }
    };

    const fetchClasses = async () => {
        try {
            const response = await api.get('/classes');
            if (response.data.success) {
                setClasses(response.data.data);
            }
        } catch (error) {
            console.error('获取班级列表失败:', error);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await api.delete(`/data-collection/tasks/${id}`);
            if (response.data.success) {
                message.success('删除成功');
                fetchTasks();
            } else {
                message.error('删除失败: ' + response.data.message);
            }
        } catch (error) {
            console.error('删除任务出错:', error);
            message.error('删除任务出错');
        }
    };

    const showPushModal = (task) => {
        setSelectedTaskId(task.id);
        setPushModalVisible(true);
        form.resetFields();
    };

    const handlePush = async () => {
        try {
            const values = await form.validateFields();
            setPushing(true);
            const response = await api.post(`/data-collection/tasks/${selectedTaskId}/push`, {
                classIds: values.classIds
            });

            if (response.data.success) {
                message.success(`成功推送至 ${response.data.successCount} 个在线班级`);
                setPushModalVisible(false);
            } else {
                message.error('推送失败: ' + response.data.message);
            }
        } catch (error) {
            if (error.errorFields) return; // 表单验证错误
            console.error('推任务出错:', error);
            message.error(error.response?.data?.message || '推送任务出错');
        } finally {
            setPushing(false);
        }
    };

    const handleQuickPublish = async () => {
        try {
            const values = await quickForm.validateFields();
            setQuickPushing(true);

            // 找到选中的模板
            const selectedTemplate = QUICK_TEMPLATES.find(t => t.id === values.templateId);
            if (!selectedTemplate) return;

            // 1. 创建任务
            const taskPayload = {
                title: selectedTemplate.title,
                description: selectedTemplate.description,
                fields: selectedTemplate.fields,
                target: values.target,
                deadline: values.deadline ? values.deadline.format('YYYY-MM-DD HH:mm:ss') : null,
                status: 'active'
            };

            const createRes = await api.post('/data-collection/tasks', taskPayload);

            if (!createRes.data.success) {
                message.error('创建任务失败: ' + createRes.data.message);
                setQuickPushing(false);
                return;
            }

            const newTaskId = createRes.data.data.id;

            // 2. 推送任务
            const pushRes = await api.post(`/data-collection/tasks/${newTaskId}/push`, {
                classIds: values.classIds
            });

            if (pushRes.data.success) {
                message.success('已成功应用模板并推送至选中班级!');
                setQuickModalVisible(false);
                fetchTasks(); // 刷新列表
            } else {
                message.error('推送失败，但任务已创建: ' + pushRes.data.message);
            }
        } catch (error) {
            if (error.errorFields) return;
            console.error('快捷推送出错:', error);
            message.error('快捷下发操作出错');
        } finally {
            setQuickPushing(false);
        }
    };

    const columns = [
        {
            title: '序号',
            key: 'index',
            render: (text, record, index) => index + 1,
            width: 60,
        },
        {
            title: '表单标题',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: '收集目标',
            dataIndex: 'target',
            key: 'target',
            render: (text) => {
                const targetMap = {
                    'desktop': <Tag color="blue">教室桌面端</Tag>,
                    'wechat_parent': <Tag color="green">家长小程序</Tag>,
                    'all': <Tag color="purple">全部(桌面+小程序)</Tag>
                };
                return targetMap[text] || text;
            }
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const color = status === 'active' ? 'processing' : (status === 'draft' ? 'default' : 'success');
                const text = status === 'active' ? '收集中' : (status === 'draft' ? '草稿' : '已结束');
                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: '截止日期',
            dataIndex: 'deadline',
            key: 'deadline',
            render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm') : '无',
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => showPreviewModal(record)}
                        title="预览表单内容"
                    >
                        预览
                    </Button>
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={() => showPushModal(record)}
                        disabled={record.status !== 'active'}
                        title="推送收集任务至对应终端"
                    >
                        派发任务
                    </Button>
                    <Button
                        icon={<BarChartOutlined />}
                        onClick={() => navigate(`/data-collection/results/${record.id}`)}
                    >
                        查看结果
                    </Button>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/data-collection/edit/${record.id}`)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除这个收集任务吗？相关的提交记录也会被删除！"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button danger icon={<DeleteOutlined />}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false}>
            {/* 顶部的快捷模板区域 */}
            <div style={{ marginBottom: 32 }}>
                <Title level={5} style={{ marginBottom: 16 }}>推荐使用预设模板快速下发任务</Title>
                <Row gutter={[16, 16]}>
                    {QUICK_TEMPLATES.map(t => (
                        <Col span={4} key={t.id}>
                            <Card
                                hoverable
                                size="small"
                                onClick={() => {
                                    // 导航到创建页，利用 state 传递默认模板数据，从而实现可编辑
                                    navigate('/data-collection/create', { state: { template: t } });
                                }}
                                style={{
                                    height: '100%',
                                    borderColor: '#b7eb8f',
                                    backgroundColor: '#f6ffed',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}
                            >
                                <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: 4, color: '#389e0d' }}>{t.title}</div>
                                <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>{t.description}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={4}>数据收集任务管理</Title>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/data-collection/create')}
                    >
                        新建空白收集任务
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={tasks}
                rowKey="id"
                loading={loading}
                pagination={{ defaultPageSize: 10 }}
            />

            <Modal
                title="派遣收集任务到班级"
                open={pushModalVisible}
                onOk={handlePush}
                onCancel={() => setPushModalVisible(false)}
                confirmLoading={pushing}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="classIds"
                        label="勾选要派发任务的班级"
                        rules={[{ required: true, message: '请至少选择一个班级！' }]}
                    >
                        <Checkbox.Group style={{ width: '100%' }}>
                            <Row gutter={[8, 8]}>
                                {classes.map(c => (
                                    <Col span={8} key={c.id}>
                                        <Checkbox value={c.id}>{c.name}</Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </Form.Item>
                    <div style={{ color: '#888', marginTop: '10px' }}>
                        提示：推送到教室后，学生可以在大屏上直接触摸操作并提交数据；包含家长微信端的，也会立即给绑定的家长发微信提醒。
                    </div>
                </Form>
            </Modal>

            <Modal
                title={`表单预览`}
                open={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setPreviewModalVisible(false)}>
                        关闭预览
                    </Button>
                ]}
                width={600}
            >
                {previewTask && (
                    <div style={{ padding: '10px 0' }}>
                        <Title level={4} style={{ textAlign: 'center', marginBottom: 16 }}>{previewTask.title}</Title>
                        {previewTask.description && (
                            <div style={{ color: '#666', marginBottom: 20, textAlign: 'center' }}>{previewTask.description}</div>
                        )}
                        <Form layout="vertical">
                            {previewTask.fields && previewTask.fields.map((field, index) => (
                                <Form.Item
                                    key={field.id || index}
                                    label={field.label}
                                    required={field.required}
                                >
                                    {field.type === 'text' && <Input size="large" disabled placeholder="文本输入框" />}
                                    {field.type === 'number' && <Input size="large" type="number" disabled placeholder="数字输入框" />}
                                    {field.type === 'radio' && (
                                        <Radio.Group disabled>
                                            <Space direction="vertical">
                                                {field.options?.map(opt => <Radio key={opt} value={opt}>{opt}</Radio>)}
                                            </Space>
                                        </Radio.Group>
                                    )}
                                    {field.type === 'checkbox' && (
                                        <Checkbox.Group disabled style={{ width: '100%' }}>
                                            <Row>
                                                {field.options?.map(opt => (
                                                    <Col span={24} key={opt} style={{ marginBottom: 8 }}>
                                                        <Checkbox value={opt}>{opt}</Checkbox>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Checkbox.Group>
                                    )}
                                </Form.Item>
                            ))}
                        </Form>
                    </div>
                )}
            </Modal>

        </Card>
    );
};

export default CollectionList;
